import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc, updateDoc, serverTimestamp, collection, addDoc, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { FaUser, FaClock, FaComments, FaCheckCircle, FaFileUpload } from 'react-icons/fa';
import { createNotification, NotificationType } from '../../services/NotificationService';

function QuestionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [question, setQuestion] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const fetchQuestion = useCallback(async () => {
    try {
      const docRef = doc(db, 'questions', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setQuestion({
          id: docSnap.id,
          title: data.title || '',
          content: data.question || data.content || '',
          author: data.author || '',
          userId: data.userId || '',
          status: data.status || 'pending',
          files: data.files || [],
          createdAt: data.createdAt?.toDate() || new Date(),
          answersCount: data.answersCount || 0,
          lastAnsweredAt: data.lastAnsweredAt?.toDate() || null,
          lastAnsweredBy: data.lastAnsweredBy || null
        });
      }
    } catch (err) {
      console.error('Soru yüklenirken hata:', err);
      setError('Soru yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const handleMarkAsSolved = async () => {
    if (!user || (user.role !== 'admin' && user.role !== 'editor')) {
      setError('Bu işlem için yetkiniz yok. Sadece admin ve editörler soruları çözüldü olarak işaretleyebilir.');
      return;
    }

    try {
      const docRef = doc(db, 'questions', id);
      await updateDoc(docRef, {
        status: question.status === 'solved' ? 'pending' : 'solved',
        solvedAt: serverTimestamp(),
        solvedBy: user.uid
      });

      // Soru sahibine bildirim gönder
      if (question.userId !== user.uid && question.status !== 'solved') {
        await createNotification(question.userId, NotificationType.QUESTION_RESOLVED, {
          questionId: id,
          questionTitle: question.title || question.content,
          resolvedBy: user.displayName || user.email
        });
      }

      await fetchQuestion();
    } catch (err) {
      console.error('Durum güncellenirken hata:', err);
      setError('Soru durumu güncellenirken bir hata oluştu');
    }
  };

  useEffect(() => {
    fetchQuestion();
  }, [fetchQuestion]);

  useEffect(() => {
    if (!id) return;

    const answersRef = collection(db, 'questions', id, 'answers');
    const q = query(answersRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const answersData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          content: data.content || '',
          files: data.files || []
        };
      });
      setAnswers(answersData);
    });

    return () => unsubscribe();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setError('Yorum yapmak için lütfen giriş yapın');
      return;
    }
    if (question.status === 'solved') {
      setError('Bu soru çözüldü olarak işaretlenmiş. Yeni yanıt eklenemez.');
      return;
    }
    if (!comment.trim() && files.length === 0) {
      setError('Lütfen bir yorum yazın veya dosya ekleyin');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const answerData = {
        content: comment.trim(),
        userId: user.uid,
        author: user.displayName || user.email,
        files: files,
        createdAt: serverTimestamp()
      };

      const answersRef = collection(db, `questions/${id}/answers`);
      await addDoc(answersRef, answerData);

      setComment('');
      setFiles([]);
      setError(null);

      const questionRef = doc(db, 'questions', id);
      await updateDoc(questionRef, {
        lastAnsweredAt: serverTimestamp(),
        lastAnsweredBy: user.uid,
        answersCount: (question.answersCount || 0) + 1
      });

      if (question.userId !== user.uid) {
        await createNotification(question.userId, NotificationType.QUESTION_ANSWERED, {
          questionId: id,
          questionTitle: question.title || question.content,
          answeredBy: user.displayName || user.email
        });
      }
    } catch (error) {
      console.error('Yorum gönderme hatası:', error);
      setError('Yorum gönderilirken bir hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!question) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          {/* Soru Başlığı ve Detayları */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-bold text-gray-900">
                {question?.title || question?.content?.substring(0, 100) || 'Başlıksız Soru'}
              </h1>
              <div className="flex items-center gap-4">
                {question?.status === 'solved' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <FaCheckCircle className="mr-1" />
                    Çözüldü
                  </span>
                )}
                {user && (user.role === 'admin' || user.role === 'editor') && (
                  <button
                    onClick={handleMarkAsSolved}
                    className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium ${
                      question.status === 'solved'
                        ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                        : 'bg-green-100 text-green-800 hover:bg-green-200'
                    }`}
                  >
                    <FaCheckCircle className="mr-2" />
                    {question.status === 'solved' ? 'Çözülmedi Olarak İşaretle' : 'Çözüldü Olarak İşaretle'}
                  </button>
                )}
              </div>
            </div>

            {/* Soru Detayları */}
            <div className="flex items-center space-x-4 text-sm text-gray-500 mb-6">
              <div className="flex items-center">
                <FaUser className="mr-2" />
                <span>{question?.author || 'Anonim'}</span>
              </div>
              <div className="flex items-center">
                <FaClock className="mr-2" />
                <span>
                  {question?.createdAt
                    ? new Date(question.createdAt).toLocaleDateString('tr-TR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : 'Tarih bilgisi yok'}
                </span>
              </div>
            </div>

            {/* Soru İçeriği */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="prose max-w-none">
                <pre className="whitespace-pre-wrap text-gray-700 font-sans">
                  {question?.content || question?.question || 'İçerik bulunamadı'}
                </pre>
              </div>

              {/* Soru Ekleri */}
              {question?.files && question.files.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Ekler:</h4>
                  <div className="flex flex-wrap gap-2">
                    {question.files.map((file, index) => (
                      <a
                        key={index}
                        href={file.data}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                      >
                        <FaFileUpload className="mr-2" />
                        {file.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Cevaplar Bölümü */}
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <FaComments className="mr-2" />
              Cevaplar ({answers.length})
            </h2>

            {answers.length > 0 ? (
              <div className="space-y-6">
                {answers.map((answer) => (
                  <div key={answer.id} className="bg-gray-50 rounded-lg p-6">
                    {/* Cevap Başlığı */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <FaUser className="text-gray-400" />
                        <span className="font-medium text-gray-900">{answer.author}</span>
                      </div>
                      <div className="text-sm text-gray-500">
                        {answer.createdAt
                          ? new Date(answer.createdAt).toLocaleDateString('tr-TR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : 'Tarih bilgisi yok'}
                      </div>
                    </div>

                    {/* Cevap İçeriği */}
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                      <p className="text-gray-700 whitespace-pre-wrap">{answer.content}</p>
                    </div>

                    {/* Cevap Ekleri */}
                    {answer.files && answer.files.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Ekler:</h4>
                        <div className="flex flex-wrap gap-2">
                          {answer.files.map((file, index) => (
                            <a
                              key={index}
                              href={file.data}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                            >
                              <FaFileUpload className="mr-2" />
                              {file.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                Henüz cevap yok.
              </div>
            )}

            {/* Yorum Formu - Sadece Üyeler Görebilir ve Soru Çözülmemişse */}
            <div className="mt-8">
              {question.status === 'solved' ? (
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <FaCheckCircle className="h-5 w-5 text-green-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-green-700">
                        Bu soru çözüldü olarak işaretlenmiş. Yeni yanıt eklenemez.
                      </p>
                    </div>
                  </div>
                </div>
              ) : user ? (
                <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow-lg">
                  <h3 className="text-lg font-semibold mb-4">Yanıt Yaz</h3>
                  <div className="mb-4">
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="4"
                      placeholder="Yanıtınızı buraya yazın..."
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <FaFileUpload className="text-gray-600" />
                      <span className="text-gray-600">Dosya Ekle</span>
                      <input
                        type="file"
                        multiple
                        onChange={(e) => {
                          const selectedFiles = Array.from(e.target.files);
                          const newFiles = selectedFiles.map(file => ({
                            name: file.name,
                            type: file.type,
                            size: file.size,
                            data: URL.createObjectURL(file)
                          }));
                          setFiles([...files, ...newFiles]);
                        }}
                        className="hidden"
                      />
                    </label>
                    {files.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600">Seçilen dosyalar:</p>
                        <ul className="list-disc list-inside">
                          {files.map((file, index) => (
                            <li key={index} className="text-sm text-gray-600">{file.name}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {error && (
                    <div className="mb-4 text-red-500 text-sm">{error}</div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className={`w-full py-2 px-4 rounded-lg text-white font-medium ${
                      submitting
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {submitting ? 'Gönderiliyor...' : 'Yanıt Gönder'}
                  </button>
                </form>
              ) : (
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <FaUser className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        Yanıt yazmak için{' '}
                        <Link to="/login" className="font-medium underline hover:text-blue-600">
                          giriş yapın
                        </Link>
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default QuestionDetail;